// ===================================
// NOTIFICATIONS SYSTEM
// Toast notifications for user feedback
// ===================================

class NotificationSystem {
  constructor() {
    this.container = null;
    this.notifications = new Map();
    this.init();
  }

  init() {
    this.createContainer();
  }

  createContainer() {
    this.container = document.createElement("div");
    this.container.id = "notification-container";
    this.container.className = "fixed top-4 right-4 z-50 space-y-2 max-w-md";
    document.body.appendChild(this.container);
  }

  show(message, type = "info", duration = 5000) {
    const notification = this.createNotification(message, type);
    const id = this.generateId();

    notification.id = `notification-${id}`;
    this.notifications.set(id, notification);

    this.container.appendChild(notification);

    // Animate in
    requestAnimationFrame(() => {
      notification.classList.add("translate-x-0", "opacity-100");
      notification.classList.remove("translate-x-full", "opacity-0");
    });

    // Auto remove
    if (duration > 0) {
      setTimeout(() => this.remove(id), duration);
    }

    return id;
  }

  createNotification(message, type) {
    const notification = document.createElement("div");
    notification.className = `
            transform transition-all duration-300 translate-x-full opacity-0
            bg-white border-l-4 rounded-lg shadow-lg p-4 min-w-0
            ${this.getTypeClasses(type)}
        `;

    const icon = this.getIcon(type);

    notification.innerHTML = `
            <div class="flex items-start">
                <div class="flex-shrink-0">
                    ${icon}
                </div>
                <div class="ml-3 flex-1">
                    <p class="text-sm font-medium text-gray-900">${message}</p>
                </div>
                <div class="ml-4 flex-shrink-0">
                    <button class="notification-close inline-flex text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                        <span class="sr-only">Close</span>
                        <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
                        </svg>
                    </button>
                </div>
            </div>
        `;

    // Add close functionality
    const closeBtn = notification.querySelector(".notification-close");
    closeBtn.addEventListener("click", () => {
      const id = parseInt(notification.id.replace("notification-", ""));
      this.remove(id);
    });

    return notification;
  }

  getTypeClasses(type) {
    const classes = {
      success: "border-green-400 bg-green-50",
      error: "border-red-400 bg-red-50",
      warning: "border-yellow-400 bg-yellow-50",
      info: "border-blue-400 bg-blue-50",
    };
    return classes[type] || classes.info;
  }

  getIcon(type) {
    const icons = {
      success: `
                <svg class="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                </svg>
            `,
      error: `
                <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
                </svg>
            `,
      warning: `
                <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                </svg>
            `,
      info: `
                <svg class="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
                </svg>
            `,
    };
    return icons[type] || icons.info;
  }

  remove(id) {
    const notification = this.notifications.get(id);
    if (notification) {
      notification.classList.add("translate-x-full", "opacity-0");
      notification.classList.remove("translate-x-0", "opacity-100");

      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
        this.notifications.delete(id);
      }, 300);
    }
  }

  removeAll() {
    this.notifications.forEach((_, id) => this.remove(id));
  }

  generateId() {
    return Date.now() + Math.random();
  }
}

// Export singleton instance
export const notifications = new NotificationSystem();
export default notifications;
